/**
 * Money helpers. Amounts are integer minor units. Currency exponents follow ISO-4217.
 */
const ZERO_DECIMAL = new Set(["BIF", "CLP", "DJF", "GNF", "ISK", "JPY", "KMF", "KRW", "PYG", "RWF", "UGX", "UYI", "VND", "VUV", "XAF", "XOF", "XPF"]);
const THREE_DECIMAL = new Set(["BHD", "IQD", "JOD", "KWD", "LYD", "OMR", "TND"]);

export const SUPPORTED_CURRENCIES = [
  "USD", "EUR", "GBP", "CAD", "AUD", "NZD", "CHF", "SEK", "NOK", "DKK", "PLN", "CZK", "HUF", "RON",
  "JPY", "CNY", "HKD", "SGD", "INR", "IDR", "MYR", "PHP", "THB", "KRW",
  "AED", "SAR", "QAR", "KWD", "BHD", "OMR", "JOD", "EGP", "MAD", "TND", "NGN", "KES", "ZAR", "GHS",
  "BRL", "MXN", "ARS", "CLP", "COP", "PEN", "TRY", "ILS",
] as const;

export function currencyExponent(currency: string): number {
  const c = currency.toUpperCase();
  if (ZERO_DECIMAL.has(c)) return 0;
  if (THREE_DECIMAL.has(c)) return 3;
  return 2;
}

export function formatMoney(minor: number, currency: string, locale = "en"): string {
  const exp = currencyExponent(currency);
  const major = minor / 10 ** exp;
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      minimumFractionDigits: exp,
      maximumFractionDigits: exp,
    }).format(major);
  } catch {
    return `${major.toFixed(exp)} ${currency}`;
  }
}

/** Parse a decimal string ("12.50") into minor units without floating point error. */
export function parseMoney(input: string | number | null | undefined, currency: string): number | null {
  if (input === null || input === undefined) return null;
  const s = String(input).trim().replace(/,/g, "");
  if (s === "") return null;
  if (!/^-?\d+(\.\d+)?$/.test(s)) throw new Error(`Invalid amount: ${input}`);
  const exp = currencyExponent(currency);
  const negative = s.startsWith("-");
  const [intPart, frac = ""] = s.replace("-", "").split(".");
  if (frac.length > exp) throw new Error(`Too many decimal places for ${currency}`);
  const minor = Number(intPart) * 10 ** exp + Number((frac + "0".repeat(exp)).slice(0, exp) || "0");
  if (!Number.isSafeInteger(minor)) throw new Error("Amount out of range");
  return negative ? -minor : minor;
}

/** Inverse of parseMoney for form inputs. */
export function minorToDecimalString(minor: number | null | undefined, currency: string): string {
  if (minor === null || minor === undefined) return "";
  const exp = currencyExponent(currency);
  if (exp === 0) return String(minor);
  const neg = minor < 0;
  const abs = Math.abs(minor);
  const s = String(abs).padStart(exp + 1, "0");
  return `${neg ? "-" : ""}${s.slice(0, -exp)}.${s.slice(-exp)}`;
}

/** Percentage of an amount in basis points, rounded half-up to the minor unit. */
export function applyBps(amount: number, bps: number): number {
  return Math.floor((amount * bps + 5000) / 10000);
}

/** Split `total` across weights proportionally; the remainder goes to the largest weights first. Sum is exact. */
export function allocate(total: number, weights: number[]): number[] {
  const sum = weights.reduce((a, b) => a + b, 0);
  if (sum <= 0) return weights.map(() => 0);
  const raw = weights.map((w) => Math.floor((total * w) / sum));
  let remainder = total - raw.reduce((a, b) => a + b, 0);
  const order = weights.map((w, i) => [w, i] as const).sort((a, b) => b[0] - a[0]);
  for (let k = 0; remainder > 0; k = (k + 1) % order.length, remainder--) raw[order[k][1]]++;
  return raw;
}
